import { useState } from 'react'
import { formatCurrencyCompact, formatSignedCurrency } from './cfoFormat'

export type ForecastTrendTone = 'receipts' | 'net' | 'ending'
export type ForecastTrendPointKind = 'actual' | 'forecast'

export type ForecastTrendMetric = {
  id: string
  label: string
  values: Array<number | null>
  lowValues?: Array<number | null>
  highValues?: Array<number | null>
  labels: string[]
  kinds: ForecastTrendPointKind[]
  tone: ForecastTrendTone
}

type ForecastTrendGeometryPoint = {
  index: number
  x: number
  y: number
  value: number
  label: string
  kind: ForecastTrendPointKind
  lowValue: number | null
  highValue: number | null
  lowY: number | null
  highY: number | null
}

export const finiteForecastValues = (values: Array<number | null | undefined>): number[] =>
  values.filter((value): value is number => value != null && Number.isFinite(value))

export const averageAbsoluteDelta = (values: Array<number | null | undefined>): number => {
  const finiteValues = finiteForecastValues(values)
  if (finiteValues.length < 2) return 0
  const deltas: number[] = []
  for (let index = 1; index < finiteValues.length; index += 1) {
    const delta = Math.abs(finiteValues[index] - finiteValues[index - 1])
    if (Number.isFinite(delta)) deltas.push(delta)
  }
  if (!deltas.length) return 0
  return deltas.reduce((sum, value) => sum + value, 0) / deltas.length
}

export const expandedForecastBandValues = (metric: ForecastTrendMetric): { lowValues: Array<number | null>; highValues: Array<number | null> } => {
  const lowValues = [...(metric.lowValues || metric.values.map(() => null))]
  const highValues = [...(metric.highValues || metric.values.map(() => null))]
  const forecastIndexes = metric.kinds
    .map((kind, index) => kind === 'forecast' ? index : -1)
    .filter((index) => index >= 0)
  if (!forecastIndexes.length) return { lowValues, highValues }

  const volatility = averageAbsoluteDelta(metric.values)
  const isCumulativeBalance = metric.id === 'ending-cash'
  forecastIndexes.forEach((index, forecastOffset) => {
    const value = Number(metric.values[index])
    if (!Number.isFinite(value)) return
    const rawLow = Number(lowValues[index])
    const rawHigh = Number(highValues[index])
    const hasRawBand = Number.isFinite(rawLow) && Number.isFinite(rawHigh)
    const orderedLow = hasRawBand ? Math.min(rawLow, rawHigh) : value
    const orderedHigh = hasRawBand ? Math.max(rawLow, rawHigh) : value
    const rawHalfWidth = Math.max((orderedHigh - orderedLow) / 2, 0)
    const horizonRatio = forecastIndexes.length > 1 ? forecastOffset / (forecastIndexes.length - 1) : 0
    const growth = isCumulativeBalance ? 1 : 1 + horizonRatio * 2.25
    const minimumHalfWidth = isCumulativeBalance && hasRawBand
      ? rawHalfWidth
      : Math.max(Math.abs(value) * 0.018, volatility * 0.35, rawHalfWidth) * growth
    const halfWidth = Math.max(rawHalfWidth * growth, minimumHalfWidth)
    lowValues[index] = metric.id === 'receipts' ? Math.max(0, value - halfWidth) : value - halfWidth
    highValues[index] = value + halfWidth
  })

  return { lowValues, highValues }
}

export type ForecastBandSource = 'calibrated' | 'estimated'

export const forecastBandValuesForDisplay = (
  metric: ForecastTrendMetric,
  bandSource: ForecastBandSource = 'estimated',
): { lowValues: Array<number | null>; highValues: Array<number | null> } => {
  if (bandSource === 'calibrated') {
    return {
      lowValues: [...(metric.lowValues || metric.values.map(() => null))],
      highValues: [...(metric.highValues || metric.values.map(() => null))],
    }
  }
  return expandedForecastBandValues(metric)
}

export const forecastBandLabel = (bandSource: ForecastBandSource): string =>
  bandSource === 'calibrated'
    ? 'calibrated range — 80% of past outcomes landed inside'
    : 'estimated range (model not yet calibrated)'

const forecastTrendGeometry = (
  metric: ForecastTrendMetric,
  width = 100,
  height = 42,
  baselineValues: Array<number | null> | null = null,
  bandSource: ForecastBandSource = 'estimated',
): { points: ForecastTrendGeometryPoint[]; baselinePoints: Array<{ x: number; y: number }>; zeroY: number | null } => {
  const { lowValues, highValues } = forecastBandValuesForDisplay(metric, bandSource)
  const finiteValues = [
    ...metric.values,
    ...lowValues,
    ...highValues,
    ...(baselineValues || []),
  ].filter((value): value is number => value != null && Number.isFinite(value))
  if (finiteValues.length < 2) return { points: [], baselinePoints: [], zeroY: null }
  const min = Math.min(...finiteValues)
  const max = Math.max(...finiteValues)
  const span = max - min || 1
  const pad = 4
  const usableHeight = height - pad * 2
  const xStep = metric.values.length > 1 ? width / (metric.values.length - 1) : width
  const yFor = (value: number) => pad + (1 - ((value - min) / span)) * usableHeight
  const points = metric.values
    .map((value, index) => ({ value: Number(value), index }))
    .filter((point) => Number.isFinite(point.value))
    .map((point) => {
      const rawLowValue = lowValues[point.index]
      const rawHighValue = highValues[point.index]
      const hasBand = rawLowValue != null && Number.isFinite(rawLowValue) && rawHighValue != null && Number.isFinite(rawHighValue)
      const lowValue = hasBand ? Math.min(rawLowValue, rawHighValue) : null
      const highValue = hasBand ? Math.max(rawLowValue, rawHighValue) : null
      return {
        index: point.index,
        x: point.index * xStep,
        y: yFor(point.value),
        value: point.value,
        label: metric.labels[point.index] || '',
        kind: metric.kinds[point.index] || 'forecast',
        lowValue,
        highValue,
        lowY: lowValue != null ? yFor(lowValue) : null,
        highY: highValue != null ? yFor(highValue) : null,
      }
    })
  const baselinePoints = (baselineValues || [])
    .map((value, index) => ({ value: Number(value), index }))
    .filter((point) => Number.isFinite(point.value))
    .map((point) => ({ x: point.index * xStep, y: yFor(point.value) }))
  return {
    points,
    baselinePoints,
    zeroY: min < 0 && max > 0 ? yFor(0) : null,
  }
}

const pointsAttr = (points: ForecastTrendGeometryPoint[]): string =>
  points.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ')

const forecastBandPolygon = (points: ForecastTrendGeometryPoint[]): string => {
  const bandPoints = points.filter((point) => point.kind === 'forecast' && point.lowY != null && point.highY != null)
  if (bandPoints.length < 2) return ''
  const high = bandPoints.map((point) => `${point.x.toFixed(2)},${point.highY!.toFixed(2)}`)
  const low = [...bandPoints].reverse().map((point) => `${point.x.toFixed(2)},${point.lowY!.toFixed(2)}`)
  return [...high, ...low].join(' ')
}

export function ForecastTrendPanel({
  metric,
  baselineValues,
  bandSource = 'estimated',
}: {
  metric: ForecastTrendMetric
  baselineValues?: Array<number | null>
  bandSource?: ForecastBandSource
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const { points, baselinePoints, zeroY } = forecastTrendGeometry(metric, 100, 42, baselineValues || null, bandSource)
  const finite = finiteForecastValues(metric.values)
  if (points.length < 2 || finite.length < 2) return null
  const first = finite[0]
  const last = finite[finite.length - 1]
  const delta = last - first
  const firstLabel = metric.labels[0] || ''
  const lastLabel = metric.labels[metric.labels.length - 1] || ''
  const actualPoints = points.filter((point) => point.kind === 'actual')
  const lastActualPoint = [...points].reverse().find((point) => point.kind === 'actual') || null
  const forecastPoints = [
    ...(lastActualPoint ? [lastActualPoint] : []),
    ...points.filter((point) => point.kind === 'forecast'),
  ]
  const band = forecastBandPolygon(points)
  const hoverPoint = hoverIndex == null ? null : points.find((point) => point.index === hoverIndex) || null
  return (
    <div className="cfo-forecast-trend" data-tone={metric.tone} onMouseLeave={() => setHoverIndex(null)}>
      <div className="cfo-forecast-trend-head">
        <span>{metric.label}</span>
        <strong>{formatCurrencyCompact(last)}</strong>
      </div>
      <svg className="cfo-forecast-trend-svg" viewBox="0 0 100 42" preserveAspectRatio="none" role="img" aria-label={`${metric.label} forecast trend`}>
        {band ? (
          <polygon className="cfo-forecast-trend-band" points={band}>
            <title>{forecastBandLabel(bandSource)}</title>
          </polygon>
        ) : null}
        {zeroY != null ? <line className="cfo-forecast-trend-zero" x1="0" x2="100" y1={zeroY} y2={zeroY} /> : null}
        {baselinePoints.length >= 2 ? (
          <polyline
            className="cfo-forecast-trend-line baseline"
            points={baselinePoints.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ')}
          >
            <title>Model baseline before driver edits</title>
          </polyline>
        ) : null}
        {actualPoints.length >= 2 ? <polyline className="cfo-forecast-trend-line actual" points={pointsAttr(actualPoints)} /> : null}
        {forecastPoints.length >= 2 ? <polyline className="cfo-forecast-trend-line forecast" points={pointsAttr(forecastPoints)} /> : null}
        {points.map((point) => (
          <circle
            key={`${metric.id}-${point.index}`}
            className="cfo-forecast-trend-hit"
            cx={point.x}
            cy={point.y}
            r={4.8}
            tabIndex={0}
            aria-label={`${metric.label} ${point.kind} ${point.label}: ${formatCurrencyCompact(point.value)}`}
            onMouseEnter={() => setHoverIndex(point.index)}
            onFocus={() => setHoverIndex(point.index)}
            onBlur={() => setHoverIndex(null)}
          />
        ))}
      </svg>
      {hoverPoint ? (
        <div
          className="cfo-forecast-trend-tooltip"
          style={{ left: `${hoverPoint.x}%`, top: `${Math.max(6, Math.min(50, hoverPoint.y * (64 / 42)))}px` }}
        >
          <span>{hoverPoint.kind === 'actual' ? 'Actual' : 'Forecast'} · {hoverPoint.label}</span>
          <strong>{formatCurrencyCompact(hoverPoint.value)}</strong>
          {hoverPoint.kind === 'forecast' && hoverPoint.lowValue != null && hoverPoint.highValue != null ? (
            <small title={forecastBandLabel(bandSource)}>
              {formatCurrencyCompact(hoverPoint.lowValue)} to {formatCurrencyCompact(hoverPoint.highValue)} · {bandSource === 'calibrated' ? 'calibrated range' : 'estimated range'}
            </small>
          ) : null}
        </div>
      ) : null}
      <div className="cfo-forecast-trend-foot">
        <span>{firstLabel}</span>
        <b>{formatSignedCurrency(delta)}</b>
        <span>{lastLabel}</span>
      </div>
    </div>
  )
}
